import { useMemo, type ReactNode } from 'react'
import type { OpeningType } from '~/components/ThreeDModel/furnitureConfig'
import type { RackColumnConfiguration } from '~/types/rackConfigurationTypes'
import { usePricingQuoteSync } from '~/utils/pricingQuote'
import { computeRackParts } from '~/utils/rackParts'
import { QuoteProvider } from '~/context/pricingQuoteContext'

interface RackQuoteProviderProps {
  sku: string
  chipboardId?: string
  width: number
  height: number
  depth: number
  plintHeight: number
  columns: number
  openingType: OpeningType
  columnWidths?: number[]
  columnConfigurations?: RackColumnConfiguration[]
  children: ReactNode
}

/**
 * Computes the rack's PAL panels and fires a live pricing-quote on every config
 * change, publishing the result through the shared QuoteContext (same one the
 * wardrobe/stand use) so ProductPrice shows the total + breakdown.
 */
export function RackQuoteProvider({
  sku,
  chipboardId,
  width,
  height,
  depth,
  plintHeight,
  columns,
  openingType,
  columnWidths,
  columnConfigurations,
  children,
}: RackQuoteProviderProps) {
  const { panels, doorsNumber, zones } = useMemo(
    () =>
      computeRackParts({
        width,
        height,
        depth,
        plintHeight,
        columnConfigurations,
        columnWidths,
      }),
    [width, height, depth, plintHeight, columnConfigurations, columnWidths]
  )

  const quote = usePricingQuoteSync({
    sku,
    chipboardId,
    width,
    height,
    depth,
    handleType: openingType,
    doorsNumber,
    sections: columns,
    parts: panels,
    zones,
  })

  return <QuoteProvider value={quote}>{children}</QuoteProvider>
}
